import React from "react";
import { useParams } from "react-router-dom";
import Checkbox from "@cs/components/src/Checkbox";
import Input from "@cs/components/src/Input";
import ListInput from "@cs/components/src/ListInput";
import Section from "@cs/components/src/Section";
import Select, { Option } from "@cs/components/src/Select";
import { linkedVariableTypeOptions } from "./utils";
import VariableInput from "../shared/VariableInput";
import OptionInput, { TwoInputWrapper } from "@cs/components/src/OptionInput";
import ApiService from "../../services/ApiService";

type Value = PluginConfigItem | ScriptConfigItem;

interface Props<T extends Value> {
  value: T;
  onChange: (value: T) => void;
}

interface LinkedVariable {
  variableConfigId?: string;
  type?: string;
}

const LinkedVariableInput = (props: {
  config?: ScriptConfigItem;
  context?: string;
  value: LinkedVariable;
  onChange: (option: LinkedVariable) => void;
}) => {
  const { value, onChange, config, context } = props;

  return (
    <TwoInputWrapper>
      <Select
        placeholder="Type"
        required
        options={linkedVariableTypeOptions.filter(
          ({ context: c }) => c === context
        )}
        value={value.type}
        onChange={({ value: v }) => onChange({ ...value, type: v ?? "" })}
      />
      <Select
        placeholder="Variable"
        required
        value={value.variableConfigId}
        options={config?.variables
          .filter((variable) =>
            linkedVariableTypeOptions
              .find(({ value: v }) => v === value.type)
              ?.filter(variable)
          )
          .map((variable) => ({
            label: variable.title || "Unnamed variable",
            value: variable.id ?? variable.key,
          }))}
        onChange={({ value: v }) =>
          onChange({ ...value, variableConfigId: v ?? "" })
        }
      />
    </TwoInputWrapper>
  );
};

const VariableForm = <T extends Value>(props: Props<T>) => {
  const { value: config, onChange } = props;
  const { componentId: id } = useParams();

  let context = config.type as string;
  let allVariables: VariableConfig[] = [...config.variables];
  if ("components" in config) {
    config.components.forEach((component) => {
      if ("variables" in component) {
        if (component.variables.some(({ id: i }) => i === id)) {
          context = component.type;
        }
        allVariables = [...allVariables, ...component.variables];
      }
    });
  }

  let extraTypes: Option[] = [];

  if ("components" in config) {
    config.components.forEach((component) => {
      if (component.type === "list_input_config") {
        extraTypes = [
          ...extraTypes,
          {
            label: component.title || "Unnamed List Input",
            value: `list_input_${component.id}`,
            group: "List Input",
          },
        ];
      }
    });
  }

  const variable: VariableConfig | undefined = allVariables.find(
    ({ id: i, key: k }) => (i ?? k) === id
  );

  const update = (key: keyof VariableConfig) => (value: unknown) => {
    const varMap = (variable: VariableConfig) =>
      (variable.id ?? variable.key) === id
        ? {
            ...variable,
            [key]: value,
          }
        : variable;
    const object = {
      ...config,
      variables: config.variables.map(varMap),
    };
    if (
      config.type === "iconik_plugin_config" &&
      object.type === "iconik_plugin_config"
    ) {
      object.components = config.components.map((component) =>
        "variables" in component
          ? {
              ...component,
              variables: component.variables.map(varMap),
            }
          : component
      );
    }
    onChange(object);
  };

  if (!variable) {
    return <></>;
  }

  return (
    <Section title="Variable" color="var(--variable)" pad>
      <Input
        label="Name"
        required
        value={variable.title}
        onChange={update("title")}
        autoFocus
      />
      {context !== "metadata_view_config" && (
        <Input
          label="Script Key"
          value={variable.key}
          onChange={update("key")}
        />
      )}
      <Select
        label="Type"
        value={variable.dataType}
        optionsAsync={{
          key: `_/option/${context}/${extraTypes.length}`,
          fn: async () => [
            ...(
              await ApiService.fetch<{ data: Option[] }>(
                "GET",
                `/api/select_option/_/option`
              )
            ).data.filter(
              (value) =>
                !value.metadata?.componentTypes ||
                value.metadata?.componentTypes.includes(context)
            ),
            ...extraTypes,
          ],
        }}
        onChange={({ value }) => update("dataType")(value)}
      />
      {(variable.dataType === "dropdown" ||
        variable.dataType === "dropdown_list") &&
        !variable.linkedVariables?.some(
          ({ type }) => type === "dropdown_option_list"
        ) && (
          <ListInput
            label="Dropdown Options"
            value={variable.options ?? []}
            onChange={update("options")}
            newValue={() => ({ value: "", label: "" })}
            component={OptionInput}
          />
        )}

      <Checkbox
        label="Required"
        value={variable.required}
        onChange={update("required")}
      />
      <Input
        label="Description"
        value={variable.description}
        onChange={update("description")}
        textarea
      />
      {(context === "iconik_script_config" ||
        context === "custom_action_config") && (
        <ListInput
          label="Linked Variables"
          value={variable.linkedVariables ?? []}
          onChange={update("linkedVariables")}
          newValue={() => ({ type: undefined, variableConfigId: undefined })}
          component={LinkedVariableInput}
          extraProps={{ config, context }}
        />
      )}
      {config.type === "iconik_script_config" && (
        <VariableInput
          variable={variable}
          variables={config.variables.map(
            ({ id, key, defaultValue, dataType }) => ({
              variableConfigId: id ?? key,
              value: defaultValue,
              dataType,
            })
          )}
          config={config}
          value={variable.defaultValue}
          onChange={update("defaultValue")}
          label="Default Value"
          systemDomainId="_"
        />
      )}
    </Section>
  );
};

export default VariableForm;
